import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import SearchIcon from '@mui/icons-material/Search';
import {
  Autocomplete,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import { Box, Stack } from '@mui/system';

import Customer from '@/shared/types/customer.type';

type WaveUICustomersSearchBarProps = {
  searchCustomersList: Customer[];
  searchCustomerValue: string;
  selectedCustomer?: Customer | null;
  loading?: boolean;
  setSearchCustomerValue: (value: string) => void;
  setSelectedCustomerId: (value: string) => void;
  getFocusStatus?: (isFocused: boolean) => void;
};

const WaveUICustomersSearchBar: FC<WaveUICustomersSearchBarProps> = ({
  searchCustomersList,
  searchCustomerValue,
  selectedCustomer,
  loading,
  setSearchCustomerValue,
  setSelectedCustomerId,
  getFocusStatus,
}) => {
  const { t } = useTranslation(['orders', 'customer', 'common']);

  const loaderComponent = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
      }}
    >
      <CircularProgress size={20} />
      <Typography variant='body2'>{t('common:searching')}</Typography>
    </Box>
  );

  return (
    <Autocomplete
      loading={loading}
      loadingText={loaderComponent}
      options={searchCustomersList}
      inputValue={searchCustomerValue}
      value={selectedCustomer}
      filterOptions={(option) => option}
      isOptionEqualToValue={(option, value) =>
        option.in_customer_id === value.in_customer_id
      }
      getOptionLabel={(option) => {
        if (typeof option === 'object' && option !== null) {
          return option.email_address;
        }
        return '';
      }}
      renderOption={(props, option) => {
        const { first_name, last_name, email_address, in_customer_id } = option;

        return (
          <Stack component='li' {...props} key={in_customer_id}>
            <Stack sx={{ width: '100%' }}>
              <Typography variant='body1'>
                {first_name
                  ? `${first_name} ${last_name}`
                  : `${t('orders:noName')} - ${in_customer_id.split('-')[0]}`}
              </Typography>

              <Typography variant='body2' color='text.secondary' noWrap>
                {email_address}
              </Typography>
            </Stack>
          </Stack>
        );
      }}
      onFocus={() => getFocusStatus?.(true)}
      onBlur={() => getFocusStatus?.(false)}
      onChange={(_, option) =>
        setSelectedCustomerId(option?.in_customer_id || '')
      }
      onInputChange={(_, value) => {
        setSearchCustomerValue(value);
      }}
      renderInput={(params) => {
        return (
          <TextField
            {...params}
            name='searchCustomer'
            placeholder={t('orders:searchCustomer')}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <SearchIcon
                    sx={{
                      width: 15,
                      height: 15,
                      color: 'text.secondary',
                    }}
                  />
                  {params.InputProps.startAdornment}
                </>
              ),
            }}
          />
        );
      }}
    />
  );
};

export default WaveUICustomersSearchBar;
